// src/views/importacoes/ImportMonitor.jsx
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  CRow, CCol, CCard, CCardHeader, CCardBody,
  CFormInput, CFormSelect, CButton, CBadge,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CProgress, CProgressBar, CModal, CModalHeader, CModalBody, CModalTitle,
  CAlert, CSpinner, CInputGroup
} from '@coreui/react';

const STATUS_EXEC_OPTIONS = [
  { v: '', label: 'Todos' },
  { v: 'queued', label: 'Aguardando' },
  { v: 'running', label: 'Em andamento' },
  { v: 'partial', label: 'Concluída (parcial)' },
  { v: 'done', label: 'Concluída (ok)' },
  { v: 'error', label: 'Concluída (erro)' },
];

const STATUS_PLAN_OPTIONS = [
  { v: '', label: 'Todas' },
  { v: 'queued', label: 'Aguardando' },
  { v: 'running', label: 'Em andamento' },
  { v: 'done', label: 'Concluída (ok)' },
  { v: 'error', label: 'Concluída (erro)' },
];

const EVENT_TIPO_OPTIONS = ['', 'INFO', 'WARN', 'ERROR', 'DEBUG', 'OMIE_REQ', 'OMIE_RESP'];
const EVENT_FASE_OPTIONS = ['', 'HEADER', 'VALIDACAO', 'ITEMS', 'OMIE'];

const fmtDateTime = (d) => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-';
const pctColor = (status) =>
  status === 'done' ? 'success' :
  status === 'error' ? 'danger' :
  status === 'running' ? 'info' : 'secondary';

const statusBadgeColor = (status) =>
  ({ queued: 'secondary', running: 'info', done: 'success', error: 'danger', partial: 'warning' }[status] || 'secondary');

const deriveUF = (nomeArquivo) => {
  const m = /^([A-Z]{2})[_-]/i.exec(nomeArquivo || '');
  return m ? m[1].toUpperCase() : '';
};

const safeArray = (value) => Array.isArray(value) ? value : [];

async function parseJsonSafe(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

export default class ImportMonitor extends Component {
  static propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
  };

  state = {
    filtroExecStatus: '',
    filtroPlanStatus: '',
    filtroUF: '',
    filtroSearch: '',
    filtroFrom: '',
    filtroTo: '',
    execucoes: [],
    selectedExecId: '',
    execucao: null,
    loadingList: false,
    loadingExec: false,
    polling: null,
    showEventos: false,
    eventos: [],
    eventosLoading: false,
    eventosPlanilha: null,
    eventosError: '',
    evTipo: '',
    evFase: '',
    evSearch: '',
    ufOptions: [],
  };

  async componentDidMount() {
    await this.fetchUFOptions();
    this.tryFetchExecucoes();
  }

  componentWillUnmount() {
    if (this.state.polling) clearInterval(this.state.polling);
  }

  fetchUFOptions = async () => {
    try {
      const r = await fetch('https://integradorbaoba.masterdaweb.net/api/pedidos/ufs');
      if (r.ok) {
        const list = await r.json();
        this.setState({ ufOptions: Array.isArray(list) ? list : [] });
        return;
      }
      this.setState({ ufOptions: [] });
    } catch {
      this.setState({ ufOptions: [] });
    }
  };

  tryFetchExecucoes = async () => {
    this.setState({ loadingList: true });
    try {
      const params = new URLSearchParams({
        limit: 50,
        status: this.state.filtroExecStatus || '',
        from: this.state.filtroFrom || '',
        to: this.state.filtroTo || '',
        uf: this.state.filtroUF || '',
      });
      const r = await fetch(`https://integradorbaoba.masterdaweb.net/api/pedidos/execucoes?${params.toString()}`);
      if (r.ok) {
        const data = await r.json();
        this.setState({ execucoes: Array.isArray(data) ? data : [], loadingList: false });
        return;
      }
      this.setState({ execucoes: [], loadingList: false });
    } catch {
      this.setState({ execucoes: [], loadingList: false });
    }
  };

  fetchExecucao = async (execId, startPolling = true) => {
    if (!execId) return;
    this.setState({ loadingExec: true });
    try {
      const r = await fetch(`https://integradorbaoba.masterdaweb.net/api/pedidos/status/${execId}`);
      if (!r.ok) throw new Error('Falha ao carregar execução');
      const execucao = await r.json();
      this.setState({ execucao, loadingExec: false });

      if (startPolling) {
        if (this.state.polling) clearInterval(this.state.polling);
        if (['queued', 'running'].includes(execucao.status)) {
          const polling = setInterval(async () => {
            try {
              const rr = await fetch(`https://integradorbaoba.masterdaweb.net/api/pedidos/status/${execId}`);
              if (!rr.ok) return;
              const ex = await rr.json();
              this.setState({ execucao: ex });
              if (!['queued', 'running'].includes(ex.status)) {
                clearInterval(this.state.polling);
                this.setState({ polling: null });
              }
            } catch {
              // mantém o polling até a próxima tentativa
            }
          }, 2000);
          this.setState({ polling });
        }
      }
    } catch {
      this.setState({ execucao: null, loadingExec: false });
    }
  };

  fetchEventos = async (planilha) => {
    this.setState({
      eventosLoading: true,
      eventosPlanilha: planilha,
      showEventos: true,
      eventos: [],
      eventosError: '',
      evTipo: '',
      evFase: '',
      evSearch: '',
    });

    try {
      const r = await fetch(`https://integradorbaoba.masterdaweb.net/api/pedidos/planilha/${planilha.id}/eventos`);
      const data = await parseJsonSafe(r);

      if (!r.ok) {
        const details = data?.details || data?.error || data?.message || data?.raw || `HTTP ${r.status}`;
        this.setState({
          eventos: [],
          eventosLoading: false,
          eventosError: `Erro ao carregar eventos da planilha #${planilha.id}: ${details}`,
        });
        return;
      }

      const eventos = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.eventos)
            ? data.eventos
            : [];

      this.setState({
        eventos,
        eventosLoading: false,
        eventosError: Array.isArray(data) || eventos.length || data === null
          ? ''
          : 'A API retornou um formato inesperado para eventos.',
      });
    } catch (error) {
      this.setState({
        eventos: [],
        eventosLoading: false,
        eventosError: `Falha de rede ao carregar eventos: ${error.message || 'erro desconhecido'}`,
      });
    }
  };

  onFilterChange = (field) => (e) => {
    this.setState({ [field]: e.target.value });
  };

  onApplyListFilters = async () => {
    await this.tryFetchExecucoes();
  };

  onSelectExec = async (e) => {
    const id = e.target.value;
    this.setState({ selectedExecId: id }, () => {
      if (id) this.fetchExecucao(id, true);
    });
  };

  onManualExecLoad = async () => {
    const id = this.state.selectedExecId;
    if (id) this.fetchExecucao(id, true);
  };

  downloadPlanilhaErrorsCSV = (planilhaId) => {
    const eventos = safeArray(this.state.eventos);
    if (!eventos.length) return;

    const rows = [['planilha_id', 'tipo', 'fase', 'linha', 'mensagem', 'payload', 'created_at']];
    eventos.filter((e) => e.tipo === 'ERROR').forEach((e) => {
      rows.push([
        planilhaId,
        e.tipo || '',
        e.fase || '',
        e.linha ?? '',
        (e.mensagem || '').replace(/\s+/g, ' ').trim(),
        e.payload ? JSON.stringify(e.payload) : '',
        e.created_at || '',
      ]);
    });

    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `erros_planilha_${planilhaId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  filteredPlanilhas = () => {
    const { execucao, filtroPlanStatus, filtroUF, filtroSearch } = this.state;
    const planilhas = safeArray(execucao?.importacao_planilhas);
    const term = (filtroSearch || '').toLowerCase();

    return planilhas
      .filter((p) => !filtroPlanStatus || p.status === filtroPlanStatus)
      .filter((p) => !filtroUF || (p.uf || deriveUF(p.nome_arquivo)) === filtroUF)
      .filter((p) => {
        if (!term) return true;
        const base = `${p.nome_arquivo} ${p.status} ${p.mensagem || ''}`.toLowerCase();
        return base.includes(term);
      })
      .sort((a, b) => (a.id < b.id ? 1 : -1));
  };

  allUFsFromExec = () => {
    const planilhas = safeArray(this.state.execucao?.importacao_planilhas);
    const set = new Set(
      planilhas
        .map((p) => (p.uf || deriveUF(p.nome_arquivo) || '').toString().toUpperCase())
        .filter(Boolean)
    );
    return Array.from(set).sort();
  };

  filteredEventos = () => {
    const { eventos, evTipo, evFase, evSearch } = this.state;
    const lista = safeArray(eventos);
    const t = (evSearch || '').toLowerCase();

    return lista
      .filter((e) => !evTipo || e.tipo === evTipo)
      .filter((e) => !evFase || e.fase === evFase)
      .filter((e) => {
        if (!t) return true;
        const mensagem = (e.mensagem || '').toLowerCase();
        const payload = JSON.stringify(e.payload || {}).toLowerCase();
        return mensagem.includes(t) || payload.includes(t);
      });
  };

  renderTopFilters() {
    const {
      filtroExecStatus, filtroPlanStatus, filtroUF, filtroFrom, filtroTo, filtroSearch,
      execucoes, loadingList, selectedExecId,
    } = this.state;

    return (
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Monitor de Importações — Filtros</strong>
        </CCardHeader>
        <CCardBody>
          <CRow className="g-3">
            <CCol md={2}>
              <label className="form-label">Status da Execução</label>
              <CFormSelect value={filtroExecStatus} onChange={this.onFilterChange('filtroExecStatus')}>
                {STATUS_EXEC_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </CFormSelect>
            </CCol>
            <CCol md={2}>
              <label className="form-label">Status das Planilhas</label>
              <CFormSelect value={filtroPlanStatus} onChange={this.onFilterChange('filtroPlanStatus')}>
                {STATUS_PLAN_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </CFormSelect>
            </CCol>
            <CCol md={2}>
              <label className="form-label">UF</label>
              <CFormSelect value={filtroUF} onChange={this.onFilterChange('filtroUF')}>
                <option value="">Todas</option>
                {this.state.ufOptions.map((uf) => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md={2}>
              <label className="form-label">De</label>
              <CFormInput type="date" value={filtroFrom} onChange={this.onFilterChange('filtroFrom')} />
            </CCol>
            <CCol md={2}>
              <label className="form-label">Até</label>
              <CFormInput type="date" value={filtroTo} onChange={this.onFilterChange('filtroTo')} />
            </CCol>
            <CCol md={2} className="d-flex align-items-end">
              <CButton color="dark" className="w-100" onClick={this.onApplyListFilters}>
                Aplicar na lista de execuções
              </CButton>
            </CCol>

            <CCol md={6}>
              <label className="form-label">Execução</label>
              <CInputGroup>
                <CFormSelect value={selectedExecId} onChange={this.onSelectExec} disabled={loadingList}>
                  <option value="">Selecione (ou informe o ID ao lado)</option>
                  {execucoes.map((ex) => (
                    <option key={ex.id} value={ex.id}>
                      #{ex.id} • {fmtDateTime(ex.inicio_processamento)} • {ex.total_planilhas} planilhas • {ex.status}
                    </option>
                  ))}
                </CFormSelect>
                <CFormInput type="number" placeholder="ID manual" value={selectedExecId} onChange={this.onSelectExec} />
                <CButton color="secondary" onClick={this.onManualExecLoad}>Carregar</CButton>
              </CInputGroup>
              {loadingList && <small className="text-muted">Carregando execuções…</small>}
            </CCol>

            <CCol md={6}>
              <label className="form-label">Busca (arquivo, status, mensagem)</label>
              <CFormInput
                placeholder="Digite para filtrar planilhas desta execução..."
                value={filtroSearch}
                onChange={this.onFilterChange('filtroSearch')}
              />
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    );
  }

  renderExecResumo() {
    const { execucao, loadingExec } = this.state;
    if (loadingExec) return <CAlert color="info"><CSpinner size="sm" /> Carregando execução…</CAlert>;
    if (!execucao) return null;

    return (
      <CRow className="mb-3">
        <CCol md={3}>
          <CCard className="text-center">
            <CCardBody>
              <div className="mb-1 text-muted">Execução</div>
              <h5>#{execucao.id} <CBadge color={statusBadgeColor(execucao.status)}>{execucao.status}</CBadge></h5>
              <div className="small">Início: {fmtDateTime(execucao.inicio_processamento)}</div>
              <div className="small">Fim: {fmtDateTime(execucao.fim_processamento)}</div>
              <div className="small">Duração: {execucao.duracao || '-'}</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center">
            <CCardBody>
              <div className="mb-1 text-muted">Planilhas</div>
              <h5>{execucao.planilhas_concluidas}/{execucao.total_planilhas}</h5>
              <CProgress className="mt-2" style={{ height: 6 }}>
                <CProgressBar value={execucao.total_planilhas ? (execucao.planilhas_concluidas / execucao.total_planilhas) * 100 : 0} />
              </CProgress>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center">
            <CCardBody>
              <div className="mb-1 text-muted">Sucessos</div>
              <h5 className="text-success">{execucao.sucessos_total || 0}</h5>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center">
            <CCardBody>
              <div className="mb-1 text-muted">Falhas</div>
              <h5 className="text-danger">{execucao.falhas_total || 0}</h5>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    );
  }

  renderPlanilhasTabela() {
    const lista = this.filteredPlanilhas();
    if (!this.state.execucao) return null;

    return (
      <CCard>
        <CCardHeader>
          <strong>Planilhas da Execução</strong>
        </CCardHeader>
        <CCardBody>
          <CTable striped hover responsive>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Arquivo</CTableHeaderCell>
                <CTableHeaderCell>UF</CTableHeaderCell>
                <CTableHeaderCell>Status</CTableHeaderCell>
                <CTableHeaderCell>Progresso</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Itens</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Sucessos</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Falhas</CTableHeaderCell>
                <CTableHeaderCell>Duração</CTableHeaderCell>
                <CTableHeaderCell>Ações</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {lista.length ? lista.map((p) => (
                <CTableRow key={p.id}>
                  <CTableDataCell title={p.mensagem || ''}>{p.nome_arquivo}</CTableDataCell>
                  <CTableDataCell>{p.uf || deriveUF(p.nome_arquivo) || '-'}</CTableDataCell>
                  <CTableDataCell>
                    <CBadge color={statusBadgeColor(p.status)}>{p.status}</CBadge>
                  </CTableDataCell>
                  <CTableDataCell style={{ minWidth: 160 }}>
                    <CProgress>
                      <CProgressBar color={pctColor(p.status)} value={p.progresso || 0}>
                        {p.progresso || 0}%
                      </CProgressBar>
                    </CProgress>
                  </CTableDataCell>
                  <CTableDataCell className="text-center">
                    {(p.itens_processados || 0)}/{(p.total_itens || 0)}
                  </CTableDataCell>
                  <CTableDataCell className="text-center text-success">{p.sucessos || 0}</CTableDataCell>
                  <CTableDataCell className="text-center text-danger">{p.falhas || 0}</CTableDataCell>
                  <CTableDataCell>{p.duracao || '-'}</CTableDataCell>
                  <CTableDataCell>
                    <CButton color="info" size="sm" className="me-2" onClick={() => this.fetchEventos(p)}>Eventos</CButton>
                    <CButton color="dark" size="sm" variant="outline" onClick={() => this.fetchEventos(p).then(() => this.downloadPlanilhaErrorsCSV(p.id))}>
                      Exportar Erros
                    </CButton>
                  </CTableDataCell>
                </CTableRow>
              )) : (
                <CTableRow>
                  <CTableDataCell colSpan={9} className="text-center text-muted">
                    Nenhuma planilha encontrada com os filtros atuais
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
        </CCardBody>
      </CCard>
    );
  }

  renderEventosModal() {
    const { showEventos, eventosLoading, eventosPlanilha, evTipo, evFase, evSearch, eventosError } = this.state;
    const lista = this.filteredEventos();

    return (
      <CModal visible={showEventos} onClose={() => this.setState({ showEventos: false, eventos: [], eventosPlanilha: null, eventosError: '' })} size="xl">
        <CModalHeader>
          <CModalTitle>Eventos — {eventosPlanilha ? eventosPlanilha.nome_arquivo : ''}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CRow className="g-3 mb-2">
            <CCol md={3}>
              <label className="form-label">Tipo</label>
              <CFormSelect value={evTipo} onChange={this.onFilterChange('evTipo')}>
                {EVENT_TIPO_OPTIONS.map((t) => <option key={t} value={t}>{t || 'Todos'}</option>)}
              </CFormSelect>
            </CCol>
            <CCol md={3}>
              <label className="form-label">Fase</label>
              <CFormSelect value={evFase} onChange={this.onFilterChange('evFase')}>
                {EVENT_FASE_OPTIONS.map((t) => <option key={t} value={t}>{t || 'Todas'}</option>)}
              </CFormSelect>
            </CCol>
            <CCol md={6}>
              <label className="form-label">Busca (mensagem/payload)</label>
              <CFormInput value={evSearch} onChange={this.onFilterChange('evSearch')} placeholder="Ex.: produto inválido, cliente OMIE..." />
            </CCol>
          </CRow>

          {eventosError && <CAlert color="danger">{eventosError}</CAlert>}

          {eventosLoading ? (
            <CAlert color="info"><CSpinner size="sm" /> Carregando eventos…</CAlert>
          ) : (
            <CTable striped hover responsive>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell style={{ width: 90 }}>Data</CTableHeaderCell>
                  <CTableHeaderCell style={{ width: 90 }}>Tipo</CTableHeaderCell>
                  <CTableHeaderCell style={{ width: 110 }}>Fase</CTableHeaderCell>
                  <CTableHeaderCell style={{ width: 70 }}>Linha</CTableHeaderCell>
                  <CTableHeaderCell>Mensagem</CTableHeaderCell>
                  <CTableHeaderCell>Payload</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {lista.length ? lista.map((e, i) => (
                  <CTableRow key={e.id || i}>
                    <CTableDataCell>{fmtDateTime(e.created_at)}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color={statusBadgeColor((e.tipo === 'ERROR' ? 'error' : e.tipo === 'WARN' ? 'partial' : 'running'))}>{e.tipo}</CBadge>
                    </CTableDataCell>
                    <CTableDataCell>{e.fase || '-'}</CTableDataCell>
                    <CTableDataCell>{e.linha ?? '-'}</CTableDataCell>
                    <CTableDataCell>{e.mensagem}</CTableDataCell>
                    <CTableDataCell><code style={{ whiteSpace: 'pre-wrap' }}>{e.payload ? JSON.stringify(e.payload) : ''}</code></CTableDataCell>
                  </CTableRow>
                )) : (
                  <CTableRow><CTableDataCell colSpan={6} className="text-center text-muted">Nenhum evento</CTableDataCell></CTableRow>
                )}
              </CTableBody>
            </CTable>
          )}

          <div className="mt-3 d-flex justify-content-end">
            <CButton color="secondary" className="me-2" onClick={() => this.downloadPlanilhaErrorsCSV(eventosPlanilha?.id)}>Exportar Erros (CSV)</CButton>
            <CButton color="dark" onClick={() => this.setState({ showEventos: false, eventos: [], eventosPlanilha: null, eventosError: '' })}>Fechar</CButton>
          </div>
        </CModalBody>
      </CModal>
    );
  }

  render() {
    return (
      <CRow>
        <CCol>
          {this.renderTopFilters()}
          {this.renderExecResumo()}
          {this.renderPlanilhasTabela()}
          {this.renderEventosModal()}
        </CCol>
      </CRow>
    );
  }
}
